import React, { useState, useEffect, useCallback } from 'react';
import {
    GitBranch, Zap, MessageSquare, BarChart3, Mic,
    Library, DollarSign, CheckSquare, Loader2, AlertTriangle, Info,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';

/**
 * WorkflowTab
 *
 * Workflow-level analytics showing relative volume across product areas.
 * Answers: which workflows drive activity? what's strong vs partial telemetry?
 *
 * Powered by GET /internal/analytics/workflow
 */

const WORKFLOW_ICONS = {
    lesson_generation:  Zap,
    chat_assist:        MessageSquare,
    grading_analysis:   CheckSquare,
    deliverables:       BarChart3,
    audio_voice:        Mic,
    campus_intelligence: Library,
    monetization_funnel: DollarSign,
};

const WORKFLOW_COLORS = {
    core:       '#3b82f6',
    engagement: '#10b981',
    premium:    '#8b5cf6',
    admin:      '#f59e0b',
    revenue:    '#ef4444',
};

const WorkflowTab = ({ filters = {} }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const res = await adminAnalytics.getWorkflowAnalytics(params);
            setData(res.data);
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Access denied. Admin role required.');
            } else {
                setError(err.response?.data?.detail || 'Failed to load workflow analytics.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={centerStyle}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading workflow analytics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={centerStyle}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    if (!data) return null;

    const workflows = data.workflows || [];
    const pipeline = data.lesson_pipeline || {};
    const maxVolume = Math.max(...workflows.map(w => w.volume), 1);

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GitBranch size={20} style={{ color: '#3b82f6' }} />
                    Workflow Analytics
                </h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Last {data.period_days || 7} days · Activity volume by product area
                </p>
            </div>

            {/* Data quality note */}
            <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 14px', borderRadius: 8, marginBottom: 20,
                background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 12, color: '#6b7280',
            }}>
                <Info size={14} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 1 }} />
                <span>{data.note}</span>
            </div>

            {/* Lesson Pipeline Health */}
            <div style={{ marginBottom: 28 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 12, paddingBottom: 8,
                    borderBottom: '2px solid #3b82f620',
                }}>
                    <Zap size={15} style={{ color: '#3b82f6' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Lesson Pipeline</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12 }}>
                    <MetricCard label="Lessons Generated" value={pipeline.lessons_generated ?? 0} accent="#3b82f6" />
                    <MetricCard
                        label="Pipeline Success Rate"
                        value={pipeline.success_rate_pct != null ? `${pipeline.success_rate_pct}%` : '—'}
                        accent={(pipeline.success_rate_pct ?? 100) >= 99 ? '#10b981' : '#f59e0b'}
                        sub="Strong data — user_activity_daily"
                        quality="strong"
                    />
                    <MetricCard
                        label="Pipeline Failures"
                        value={pipeline.failures ?? 0}
                        accent={(pipeline.failures ?? 0) > 0 ? '#ef4444' : '#10b981'}
                    />
                </div>
            </div>

            {/* Workflow Activity Bar Chart */}
            <div style={{ marginBottom: 28 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 12, paddingBottom: 8,
                    borderBottom: '2px solid #6366f120',
                }}>
                    <BarChart3 size={15} style={{ color: '#6366f1' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Activity by Workflow</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {workflows.map((wf) => {
                        const Icon = WORKFLOW_ICONS[wf.workflow] || BarChart3;
                        const color = WORKFLOW_COLORS[wf.category] || '#6366f1';
                        const barPct = maxVolume > 0 ? Math.max((wf.volume / maxVolume) * 100, 2) : 2;

                        return (
                            <div key={wf.workflow} style={{
                                background: '#fff', borderRadius: 10, padding: '12px 16px',
                                border: '1px solid #e5e7eb',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Icon size={15} style={{ color }} />
                                        <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                                            {wf.label}
                                        </span>
                                        {wf.data_quality === 'partial' && (
                                            <span style={{
                                                fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                                background: '#fef3c7', color: '#92400e', fontWeight: 500,
                                            }}>
                                                partial
                                            </span>
                                        )}
                                        {wf.data_quality === 'strong' && (
                                            <span style={{
                                                fontSize: 10, padding: '1px 6px', borderRadius: 4,
                                                background: '#d1fae5', color: '#065f46', fontWeight: 500,
                                            }}>
                                                strong
                                            </span>
                                        )}
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                                        {wf.volume.toLocaleString()}
                                    </span>
                                </div>
                                <div style={{
                                    height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden',
                                }}>
                                    <div style={{
                                        height: '100%', width: `${barPct}%`,
                                        background: color, borderRadius: 3,
                                        transition: 'width 0.4s ease',
                                    }} />
                                </div>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                    {wf.category} · {wf.data_quality === 'strong' ? 'user_activity_daily' : 'workspace_events'}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};


// ── Local components ────────────────────────────────────────────────────────

const centerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 300, flexDirection: 'column', gap: 10,
};

const MetricCard = ({ label, value, sub, quality }) => (
    <div style={{
        background: '#fff', borderRadius: 12, padding: '16px 18px',
        border: '1px solid #e5e7eb',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{label}</span>
            {quality === 'strong' && (
                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#d1fae5', color: '#065f46' }}>strong</span>
            )}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
);


export default WorkflowTab;
